import { AiTarget, TranscriptLine, TranscriptToolCall } from "@/types/conversation/transcript";

interface RawTranscriptItem {
  speaker?: string;
  role?: string;
  text?: string;
  message?: string;
  timestamp?: number;
  time_in_call_secs?: number;
  start?: number;
  tool_calls?: TranscriptToolCall[];
}

/**
 * Where a user might want to take a transcript to dig deeper. We don't
 * pre-fill the prompt (no keys, transcripts blow past URL limits) — we copy a
 * ready-to-paste payload to the clipboard and open the assistant in a new tab.
 */
export const AI_TARGETS: AiTarget[] = [
  { id: "chatgpt", label: "ChatGPT", url: "https://chatgpt.com/" },
  { id: "claude", label: "Claude", url: "https://claude.ai/new" },
  { id: "gemini", label: "Gemini", url: "https://gemini.google.com/app" },
  { id: "copilot", label: "Copilot", url: "https://copilot.microsoft.com/" },
];

const formatCallTime = (secs?: number) => {
  if (secs === undefined || secs === null || isNaN(secs)) return "";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/**
 * Normalize a raw transcript array (which arrives in either the ElevenLabs
 * `speaker`/`text`/`timestamp` shape or our `role`/`message`/`time_in_call_secs`
 * shape) into a single canonical `TranscriptLine[]`. Returns [] for anything
 * that isn't a populated array, so callers can treat "no transcript" uniformly.
 */
export const normalizeTranscript = (raw: unknown): TranscriptLine[] => {
  if (!Array.isArray(raw)) return [];
  return (raw as RawTranscriptItem[]).map((item) => ({
    role: item?.speaker || item?.role || "",
    message: item?.text ?? item?.message ?? "",
    time_in_call_secs: item?.timestamp ?? item?.time_in_call_secs ?? item?.start,
    tool_calls: item?.tool_calls || [],
  }));
};

/**
 * Genie conversations have two roles, `user` and `agent`. Meeting recordings
 * and genie meetings carry the speaker's own name (or "Speaker A") instead.
 */
const speakerLabel = (role: string, genieName?: string): string => {
  if (role === "user") return "Customer";
  if (!role || role === "agent" || role === "assistant") return genieName || "Genie";
  return role;
};

/** `<genie>-transcript-<yyyy-mm-dd>.txt`, safe as a download name. */
export const transcriptFileName = (genieName?: string, dateRaw?: string | null): string => {
  const date = dateRaw ? new Date(dateRaw) : null;
  const day = date && !isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : "";
  const genie = (genieName || "genie")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return [genie || "genie", "transcript", day].filter(Boolean).join("-") + ".txt";
};

export interface TranscriptTextParams {
  genieName?: string;
  /** ISO date string (conversation_date / created_at). */
  dateRaw?: string | null;
  /** Call length in seconds. */
  durationSeconds?: number | null;
  transcript: TranscriptLine[];
}

/**
 * Build the clean, paste-ready plain-text transcript:
 *
 *   <Genie> — transcript
 *   12 Jun 2026, 3:04 PM · 4m 12s
 *
 *   [0:03] Customer: ...
 *   [0:09] <Genie>: ...
 */
export const buildTranscriptText = ({
  genieName,
  dateRaw,
  durationSeconds,
  transcript,
}: TranscriptTextParams): string => {
  let dateStr = "";
  if (dateRaw) {
    try {
      dateStr = new Date(dateRaw).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
    } catch {
      dateStr = "";
    }
  }

  const durStr =
    typeof durationSeconds === "number" && durationSeconds > 0
      ? `${Math.floor(durationSeconds / 60)}m ${Math.round(durationSeconds % 60)}s`
      : "";

  const metaLine = [dateStr, durStr].filter(Boolean).join(" · ");
  const lines = transcript.map((item) => {
    const speaker = speakerLabel(item.role, genieName);
    const t = formatCallTime(item.time_in_call_secs);
    return `${t ? `[${t}] ` : ""}${speaker}: ${item.message ?? ""}`.trim();
  });

  const parts = [`${genieName || "Genie"} — transcript`];
  if (metaLine) parts.push(metaLine);
  parts.push("", ...lines);
  return parts.join("\n");
};

export const buildAnalysisPrompt = (genieName?: string, isMeeting = false) =>
  isMeeting
    ? `Below is a transcript of a meeting my Genie${genieName ? ` "${genieName}"` : ""} took notes in. ` +
      `Please summarize what was discussed, the decisions made, the action items with who owns them, any open questions, and suggested follow-ups.\n\n`
    : `Below is a transcript of a conversation between my Genie${genieName ? ` "${genieName}"` : ""} and a customer. ` +
      `Please summarize what the customer needed, how well the Genie handled it, anything it missed, and a few specific ways to improve it.\n\n`;
